import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertBox,
  Button,
  Input,
  InputGroup,
  ModalInfo,
  ModalInfoProps,
} from '@/components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { userSelectors } from '@/redux/selectors';
import { blockActions, userActions } from '@/redux/actions';
import { useInputText } from '@/hooks';
import { Validation } from '@/models';
import { AuthProviderEnum, EMAIL_REGEX, PASSWORD_REGEX } from '@/config';
import { ChangeFullNameEmailRequest } from '@/api';

import styles from './UpdatePersonalInformation.module.sass';

interface UpdatePersonalInformationProps extends ModalInfoProps {}

const UpdatePersonalInformation: FC<UpdatePersonalInformationProps> = (
  props,
) => {
  const { isOpen, onClose } = props;
  const { data, error, loading } = useAppSelector(userSelectors.getUserSlice);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const EMAIL_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'email',
        value: EMAIL_REGEX,
        message: t('translation.invalid_email'),
      },
      {
        name: 'maxLength',
        value: 200,
        message: t('translation.max_length_error', { length: 200 }),
      },
    ],
    [t],
  );

  const USERNAME_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'maxLength',
        value: 120,
        message: t('translation.max_length_error', { length: 120 }),
      },
    ],
    [t],
  );

  const fullNameField = useInputText(
    data?.fullName || '',
    USERNAME_VALIDATIONS,
  );
  const emailField = useInputText(data?.emailAddress || '', EMAIL_VALIDATIONS);

  const emailHasChanged = useMemo(
    () => emailField.value !== data?.emailAddress,
    [emailField.value],
  );

  const PASSWORD_VALIDATIONS: Validation[] = useMemo(() => {
    const rules: Validation[] = [
      ...(emailHasChanged
        ? [
            {
              name: 'required',
              value: true,
              message: t('translation.reset_pwd_error_hint_length'),
            } as Validation,
            {
              name: 'regex',
              value: PASSWORD_REGEX,
              message: t('translation.reset_pwd_error_hint_length'),
            } as Validation,
          ]
        : []),
    ];

    return rules;
  }, [t, emailHasChanged]);

  const passwordField = useInputText('', PASSWORD_VALIDATIONS);

  const PASSWORD_CONFIRM_VALIDATIONS: Validation[] = useMemo(() => {
    const rules: Validation[] = [
      ...(!!passwordField.value
        ? [
            {
              name: 'required',
              value: true,
              message: t('translation.field_is_required'),
            } as Validation,
            {
              name: 'equal',
              value: passwordField.value,
              message: t('translation.password_mismatch'),
            } as Validation,
          ]
        : []),
    ];

    return rules;
  }, [passwordField.value, t]);

  const passwordConfirmField = useInputText('', PASSWORD_CONFIRM_VALIDATIONS);

  const passwordIsRequired = useMemo(() => {
    return AuthProviderEnum.GOOGLE === data?.provider;
  }, [data?.provider]);

  const canChangeEmail = useMemo(() => {
    if (!data?.provider) {
      return true;
    }

    return [AuthProviderEnum.DEFAULT, AuthProviderEnum.GOOGLE].includes(
      data?.provider,
    );
  }, [data?.provider]);

  useEffect(() => {
    if (isOpen) {
      fullNameField.onChange(data?.fullName || '');
      emailField.onChange(data?.emailAddress || '');
      dispatch(blockActions.block());
    } else {
      dispatch(blockActions.unblock());
    }
    dispatch(userActions.setError(null));
  }, [isOpen]);

  useEffect(() => {
    if (
      (passwordIsRequired && emailField.value === data?.emailAddress) ||
      !isOpen
    ) {
      passwordField.onReset();
      passwordConfirmField.onReset();
    }
  }, [passwordIsRequired, emailField.value, isOpen]);

  const submitHandler = () => {
    if (!loading) {
      if (emailField.isValid && fullNameField.isValid) {
        if (
          passwordIsRequired &&
          emailField.value !== data?.emailAddress &&
          (!passwordField.isValid || !passwordConfirmField.isValid)
        ) {
          if (!passwordField.isValid) {
            passwordField.onBlur();
          }

          if (!passwordConfirmField.isValid) {
            passwordConfirmField.onBlur();
          }

          return;
        }

        const payload: ChangeFullNameEmailRequest = {
          fullName: fullNameField.value,
          emailAddress: emailField.value,
        };

        if (passwordIsRequired && emailHasChanged) {
          payload.password = passwordField.value;
        }

        dispatch(userActions.changeFullNameEmail(payload));
      } else {
        if (!emailField.isValid) {
          emailField.onBlur();
        }

        if (!fullNameField.isValid) {
          fullNameField.onBlur();
        }
      }
    }
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.edit_profile_link')}
      onClose={onClose}
      actions={
        <>
          <Button
            type="primary"
            corner="rect"
            disabled={loading}
            isLoading={loading}
            onClick={submitHandler}
          >
            {t('translation.change_btn')}
          </Button>
          <Button
            type="secondary"
            corner="rect"
            disabled={loading}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
    >
      {!!error && (
        <AlertBox type="danger" className={styles.alertBox}>
          {{
            description: error,
          }}
        </AlertBox>
      )}

      <div className={styles.form}>
        <InputGroup
          label={t('translation.user_fullname_label')}
          className={styles.inputGroup}
        >
          <Input
            id="name"
            name="name"
            type="text"
            value={fullNameField.value}
            onChange={fullNameField.onChange}
            onBlur={fullNameField.onBlur}
            placeholder={t('translation.name_placeholder')!}
            disabled={loading}
            isError={fullNameField.isDirty && !fullNameField.isValid}
            required
          />
          {fullNameField.isDirty && !fullNameField.isValid && (
            <AlertBox type="danger" small>
              {{ description: fullNameField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>

        <InputGroup
          label={t('translation.user_email_label')}
          className={styles.inputGroup}
        >
          <Input
            id="email"
            name="email"
            type="text"
            value={emailField.value}
            onChange={emailField.onChange}
            onBlur={emailField.onBlur}
            placeholder={t('translation.email_placeholder')!}
            disabled={loading || !canChangeEmail}
            isError={emailField.isDirty && !emailField.isValid}
            required
          />
          {emailField.isDirty && !emailField.isValid && (
            <AlertBox type="danger" small>
              {{ description: emailField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>
        {passwordIsRequired && (
          <>
            <AlertBox type="info" className={styles.alertBox}>
              {{
                description: t('translation.change_google_email_password_note'),
              }}
            </AlertBox>
            <InputGroup label={t('translation.new_pwd_label')}>
              <Input
                id="new-password"
                name="new-password"
                type="password"
                value={passwordField.value}
                onChange={passwordField.onChange}
                onBlur={passwordField.onBlur}
                placeholder={t('translation.new_password')!}
                disabled={loading || !emailHasChanged}
                autoComplete="new-password"
                isError={emailHasChanged && !passwordField.isValid}
              />
              {emailHasChanged && !passwordField.isValid && (
                <AlertBox type="danger" small>
                  {{ description: passwordField.errors[0] }}
                </AlertBox>
              )}
            </InputGroup>

            <InputGroup label={t('translation.confirm_new_pwd_label')}>
              <Input
                id="confirm-password"
                name="confirm-password"
                type="password"
                value={passwordConfirmField.value}
                onChange={passwordConfirmField.onChange}
                onBlur={passwordConfirmField.onBlur}
                placeholder={t('translation.confirm_new_password')!}
                disabled={loading || !emailHasChanged}
                isError={emailHasChanged && !passwordConfirmField.isValid}
              />
              {emailHasChanged && !passwordConfirmField.isValid && (
                <AlertBox type="danger" small>
                  {{
                    description: passwordConfirmField.errors[0],
                  }}
                </AlertBox>
              )}
            </InputGroup>
          </>
        )}
      </div>
    </ModalInfo>
  );
};

export default UpdatePersonalInformation;
